import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { LUNCH_PLAN_WAIVERS_LIST, LUNCH_PLAN_WAIVER_SUBMIT } from '../../api/constants';

// Fetch lunch plan waivers list
export const fetchLunchPlanWaivers = createAsyncThunk(
  'lunchPlanWaiver/fetchLunchPlanWaivers',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      
      const response = await apiHandler(LUNCH_PLAN_WAIVERS_LIST, 'POST', formData);
      return response.data.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lunch plan waivers');
    }
  }
);

// Submit lunch plan waiver (create/edit)
export const submitLunchPlanWaiver = createAsyncThunk(
  'lunchPlanWaiver/submitLunchPlanWaiver',
  async (waiverData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(waiverData).forEach(key => {
        if (waiverData[key] !== null && waiverData[key] !== undefined) {
          formData.append(key, waiverData[key]);
        }
      });
      
      const response = await apiHandler(LUNCH_PLAN_WAIVER_SUBMIT, 'POST', formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit lunch plan waiver');
    }
  }
);

const lunchPlanWaiverSlice = createSlice({
  name: 'lunchPlanWaiver',
  initialState: {
    waivers: [],
    meta: null,
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.submitError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch lunch plan waivers
      .addCase(fetchLunchPlanWaivers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLunchPlanWaivers.fulfilled, (state, action) => {
        state.loading = false;
        state.waivers = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchLunchPlanWaivers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit lunch plan waiver
      .addCase(submitLunchPlanWaiver.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitLunchPlanWaiver.fulfilled, (state, action) => {
        state.submitLoading = false;
        const updatedWaiver = action.payload.data;
        const existingIndex = state.waivers.findIndex(w => w.id === updatedWaiver.id);
        if (existingIndex !== -1) {
          state.waivers[existingIndex] = updatedWaiver;
        } else {
          state.waivers.unshift(updatedWaiver);
        }
      })
      .addCase(submitLunchPlanWaiver.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      });
  }
});

export const { clearErrors } = lunchPlanWaiverSlice.actions;

// Selectors
export const selectWaivers = (state) => state.lunchPlanWaiver.waivers;
export const selectMeta = (state) => state.lunchPlanWaiver.meta;
export const selectLoading = (state) => state.lunchPlanWaiver.loading;
export const selectError = (state) => state.lunchPlanWaiver.error;
export const selectSubmitLoading = (state) => state.lunchPlanWaiver.submitLoading;
export const selectSubmitError = (state) => state.lunchPlanWaiver.submitError;
export const selectWaiverById = (state, waiverId) => {
  return state.lunchPlanWaiver.waivers.find(waiver => waiver.id === parseInt(waiverId));
};

export default lunchPlanWaiverSlice.reducer;
